import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Drawer,
  Empty,
  Flex,
  Input,
  message,
  Modal,
  Select,
  Space,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  MessageOutlined,
  PaperClipOutlined,
  ReloadOutlined,
  RollbackOutlined,
  SearchOutlined,
  SendOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import feedbackApi from "../../../api/feedback";
import { usePolling } from "../../../lib/usePolling";
import ImportExportMenu from "../../../components/cms/ImportExportMenu";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import StatStrip from "../../../components/cms/StatStrip";
import ListToolbar from "../../../components/cms/ListToolbar";
import CmsTableCard from "../../../components/cms/CmsTableCard";
import { border, brand, neutral, radius, semantic, shinko, spacing, typography } from "../../../theme/tokens";
import StatusTag from "../../../components/cms/StatusTag";
import RowActions from "../../../components/cms/RowActions";

const { Title } = Typography;

/** Rời tab rồi quay lại liên tục thì cũng chỉ gọi lại danh sách tối đa 1 lần trong khoảng này. */
const LIST_REFRESH_MIN_GAP_MS = 15000;

const FILTER_DEFS = [
  { key: "search", label: "Tìm kiếm", span: { xs: 24, md: 12, lg: 10 } },
  { key: "status", label: "Trạng thái", span: { xs: 24, sm: 12, md: 8, lg: 6 } },
  { key: "customerType", label: "Đối tượng", span: { xs: 24, sm: 12, md: 8, lg: 6 } },
];

/*
 * Đối tượng gửi phản ánh = LOẠI khách, không phải trạng thái -> hiển thị tag xám,
 * không tô màu (xem quy ước ở màn Khách hàng). Giá trị khớp `customers.customer_type`.
 * Chọn "Đại lý" thì BE gộp luôn NPP (NPP là một cấp của đại lý).
 */
const CUSTOMER_TYPE_LABELS = {
  MANUAL: "Mặc định",
  CONTRACTOR: "Thợ thầu",
  DEALER: "Đại lý",
  NPP: "Nhà phân phối",
};

const CUSTOMER_TYPE_OPTIONS = [
  { value: "CONTRACTOR", label: "Thợ thầu" },
  { value: "DEALER", label: "Đại lý" },
  { value: "MANUAL", label: "Mặc định" },
];

/*
 * Người vận hành chỉ cần biết 3 mức: chưa ai xử lý · đang trao đổi · xong.
 * BE vẫn giữ 4 trạng thái để chạy tự động theo "ai vừa nhắn", CMS gộp lại khi hiển thị:
 *   WAITING_NSX                -> Chờ xử lý  (warning: việc tồn của MÌNH, phải thấy ngay)
 *   WAITING_CUSTOMER + OPEN    -> Đang xử lý/trao đổi (info)
 *   CLOSED                     -> Kết thúc   (success)
 */
const STATUS_META = {
  WAITING_NSX: { label: "Chờ xử lý", tone: "warning" },
  WAITING_CUSTOMER: { label: "Đang xử lý/trao đổi", tone: "info" },
  OPEN: { label: "Đang xử lý/trao đổi", tone: "info" },
  CLOSED: { label: "Kết thúc", tone: "success" },
};

/** `IN_PROGRESS` là giá trị gộp, BE dịch thành WAITING_CUSTOMER + OPEN. */
const STATUS_OPTIONS = [
  { value: "WAITING_NSX", label: "Chờ xử lý" },
  { value: "IN_PROGRESS", label: "Đang xử lý/trao đổi" },
  { value: "CLOSED", label: "Kết thúc" },
];

const INTERNAL_NOTE_STYLE = {
  background: neutral[100],
  border: border.base,
  color: neutral[700],
};

const ADMIN_MESSAGE_STYLE = {
  background: brand.primarySubtle,
  border: `1px solid rgba(29, 78, 216, 0.10)`,
  color: neutral[800],
};

const CUSTOMER_MESSAGE_STYLE = {
  background: neutral[0],
  border: border.hairline,
  color: neutral[800],
};

const SYSTEM_MESSAGE_STYLE = {
  background: neutral[50],
  border: border.hairline,
  color: neutral[600],
};

const isSuccessResponse = (res) => Boolean(res?.data?.status || res?.data?.success);

const getResponseData = (res) => res?.data?.data;

const getStatusMeta = (status) => STATUS_META[status] || STATUS_META.OPEN;

const buildReplyFormData = ({ messageText, fileList }) => {
  const formData = new FormData();
  formData.append("message", messageText.trim());

  fileList.forEach((file) => {
    if (file.originFileObj) {
      formData.append("attachments[]", file.originFileObj);
    }
  });

  return formData;
};

const getMessageBubbleStyle = (item) => {
  if (item.is_internal_note) {
    return INTERNAL_NOTE_STYLE;
  }

  if (item.sender_type === "ADMIN") {
    return ADMIN_MESSAGE_STYLE;
  }

  if (item.sender_type === "SYSTEM") {
    return SYSTEM_MESSAGE_STYLE;
  }

  return CUSTOMER_MESSAGE_STYLE;
};

const getSenderLabel = (item) => {
  if (item.is_internal_note) {
    return "Ghi chú nội bộ";
  }

  if (item.sender_type === "ADMIN") {
    return item.sender?.username || "Nhân viên CMS";
  }

  if (item.sender_type === "SYSTEM") {
    return "Hệ thống";
  }

  return item.sender?.customer_name || "Khách hàng";
};

const FeedbackList = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [statusFilter, setStatusFilter] = useState();
  const [customerTypeFilter, setCustomerTypeFilter] = useState();
  const [searchText, setSearchText] = useState("");
  const [replyText, setReplyText] = useState("");
  const [attachmentList, setAttachmentList] = useState([]);
  // Số tin do nhịp poll mang về trong lúc drawer đang mở — hiện thành badge, KHÔNG tự cuộn tới.
  const [newMessageCount, setNewMessageCount] = useState(0);
  /*
   * Số thứ tự request chi tiết. Màn này quản state bằng useState thủ công nên không có gì chặn
   * phản hồi cũ: đổi phiếu hoặc đóng drawer trong lúc request đang bay thì response về sau sẽ
   * ghi đè phiếu đang xem. So sánh seq trước khi setState là bỏ được kết quả lạc.
   */
  const detailSeqRef = useRef(0);
  const messagesRef = useRef(null);
  const lastListFetchRef = useRef(0);
  const [summary, setSummary] = useState({
    total: 0,
    waiting_nsx: 0,
    waiting_customer: 0,
    open: 0,
    unread_admin_total: 0,
  });

  /**
   * @param {{silent?: boolean}} options silent = làm mới nền: KHÔNG bật spinner của bảng và
   *   không toast lỗi. Dùng khi người dùng không chủ động yêu cầu (quay lại tab chẳng hạn) —
   *   bật spinner lúc đó làm bảng nháy trong khi họ đang đọc chỗ khác.
   */
  const fetchFeedbacks = async (
    page = pagination.current,
    status = statusFilter,
    search = searchText,
    customerType = customerTypeFilter,
    { silent = false } = {}
  ) => {
    if (!silent) setLoading(true);

    try {
      const params = { page, limit: pagination.pageSize };

      if (status) {
        params.status = status;
      }

      if (customerType) {
        params.customer_type = customerType;
      }

      if (search?.trim()) {
        params.search = search.trim();
      }

      const res = await feedbackApi.getList(params);

      if (!isSuccessResponse(res)) {
        message.error(res?.data?.message || "Không thể tải danh sách hội thoại");
        return;
      }

      const data = getResponseData(res) || {};
      const items = data?.items || {};
      setFeedbacks(items?.data || []);
      setPagination((prev) => ({
        ...prev,
        current: items?.current_page || page,
        total: items?.total || 0,
      }));
      setSummary({
        total: data?.summary?.total || 0,
        waiting_nsx: data?.summary?.waiting_nsx || 0,
        waiting_customer: data?.summary?.waiting_customer || 0,
        open: data?.summary?.open || 0,
        unread_admin_total: data?.summary?.unread_admin_total || 0,
      });
    } catch (error) {
      if (!silent) {
        message.error(error?.response?.data?.message || "Lỗi khi gọi API danh sách hội thoại");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  /**
   * @param {boolean} openDrawer  Mở mới hội thoại: reset ô soạn + mở drawer + đánh dấu đã đọc.
   * @param {boolean} silent      Refresh nền: không bật skeleton, không toast lỗi, giữ nháp.
   *
   * Hai điểm khác bản trước:
   *  - KHÔNG còn gọi kèm `fetchFeedbacks` ở đây. Nhịp poll gọi hàm này mỗi phút, kéo theo cả API
   *    danh sách (nặng hơn nhiều: có summary 6 count) là gấp đôi chi phí vô ích. Danh sách được
   *    làm mới ở những chỗ thực sự cần: gửi tin, đổi trạng thái, bấm Làm mới.
   *  - Có `requestSeq` chống race: đổi phiếu/đóng drawer trong lúc request đang bay thì phản hồi
   *    cũ về sau sẽ bị bỏ, không ghi đè phiếu đang xem.
   */
  const fetchThreadDetail = async (id, openDrawer = true, silent = false) => {
    if (!silent) setDetailLoading(true);

    const seq = ++detailSeqRef.current;

    try {
      // Nhịp poll dùng peek: xem mà KHÔNG đánh dấu đã đọc (xem markRead bên dưới).
      const res = await feedbackApi.getDetail(id, { peek: silent });

      if (seq !== detailSeqRef.current) return;

      if (!isSuccessResponse(res)) {
        if (!silent) message.error(res?.data?.message || "Không thể tải chi tiết hội thoại");
        return;
      }

      const data = getResponseData(res);
      applyThreadDetail(data, { openDrawer, silent });

      if (openDrawer) {
        resetReplyComposer();
        setDrawerOpen(true);
        // Người chủ động mở phiếu = đã xem -> lúc này mới đánh dấu đã đọc.
        markThreadRead(id);
        // silent: badge chưa-đọc ở bảng cần cập nhật, nhưng người dùng đang nhìn drawer —
        // bật spinner cho bảng phía sau chỉ làm màn nháy vô cớ.
        await fetchFeedbacks(pagination.current, statusFilter, searchText, customerTypeFilter, {
          silent: true,
        });
      }
    } catch (error) {
      if (seq !== detailSeqRef.current) return;
      // Phiếu bị xóa/mất quyền: đóng drawer thay vì để nhịp poll bắn lỗi mãi.
      if (error?.response?.status === 404) {
        setDrawerOpen(false);
        setSelectedThreadId(null);
        message.warning("Hội thoại này không còn tồn tại");
        return;
      }
      if (!silent) {
        message.error(error?.response?.data?.message || "Lỗi khi tải chi tiết hội thoại");
      }
    } finally {
      if (!silent) setDetailLoading(false);
    }
  };

  /**
   * Đổ dữ liệu mới vào drawer mà KHÔNG xâm phạm việc người dùng đang làm:
   * không cuộn, không reset ô soạn — tin mới chỉ được đếm vào badge để họ tự bấm xem.
   */
  const applyThreadDetail = (data, { openDrawer, silent }) => {
    setSelectedThread((prev) => {
      if (silent && !openDrawer && prev?.id === data?.id) {
        const prevCount = prev?.messages?.length || 0;
        const nextCount = data?.messages?.length || 0;
        if (nextCount > prevCount) setNewMessageCount((n) => n + (nextCount - prevCount));

        // Người khác vừa kết thúc hội thoại: composer sẽ biến mất ngay dưới tay đang soạn,
        // nên phải nói rõ vì sao. Nháp không bị xoá, mở lại hội thoại là còn.
        if (prev?.status !== "CLOSED" && data?.status === "CLOSED") {
          message.info("Hội thoại vừa được kết thúc, không gửi thêm được phản hồi.");
        }
      }
      return data;
    });
    setSelectedThreadId(data?.id ?? null);
    if (openDrawer) setNewMessageCount(0);
  };

  /** Đánh dấu đã đọc — chỉ gọi khi có hành động của NGƯỜI, không gọi trong nhịp poll. */
  const markThreadRead = async (id) => {
    try {
      await feedbackApi.markRead(id);
    } catch (error) {
      // Không chặn luồng đọc vì một lời gọi phụ; badge sẽ đúng lại ở lần mở sau.
    }
  };

  /** Người bấm badge "N tin mới": cuộn tới cuối, đánh dấu đọc, làm mới danh sách. */
  const handleSeeNewMessages = async () => {
    setNewMessageCount(0);

    const box = messagesRef.current;
    if (box) box.scrollTo({ top: box.scrollHeight, behavior: "smooth" });

    if (!selectedThreadId) return;
    await markThreadRead(selectedThreadId);
    fetchFeedbacks(pagination.current, statusFilter, searchText, customerTypeFilter, {
      silent: true,
    });
  };

  useEffect(() => {
    fetchFeedbacks(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, customerTypeFilter]);

  /*
   * Tin của khách tự về trong lúc drawer đang mở — CHỈ khi drawer mở, chỉ ở chi tiết.
   * Danh sách cố tình không poll: API đó nặng hơn nhiều (kèm summary 6 count) mà nhân sự
   * biết có phản ánh mới qua tin nhóm Zalo rồi.
   *
   * `silent = true` -> dùng ?peek=1 (không đánh dấu đã đọc) + không toast lỗi + giữ nháp.
   */
  usePolling(() => fetchThreadDetail(selectedThreadId, false, true), {
    enabled: drawerOpen && Boolean(selectedThreadId),
  });

  /*
   * Rời tab rồi quay lại thì làm mới DANH SÁCH — thay cho việc phải rời trang rồi vào lại.
   *
   * Ba điều kiện, thiếu cái nào là bảng nháy vô cớ:
   *  - CHỈ nghe `visibilitychange`, KHÔNG nghe `window.focus`: focus bắn mỗi lần click qua lại
   *    giữa trang và DevTools/cửa sổ khác, nên bảng quay liên tục dù chẳng ai rời tab.
   *  - BỎ QUA khi drawer đang mở: đang đọc chi tiết thì không có lý gì bảng phía sau tải lại.
   *  - Cách lần trước dưới 15s thì bỏ: chuyển qua lại nhanh không cần gọi lại API.
   * Và gọi bản `silent` để không bật spinner của bảng.
   */
  useEffect(() => {
    const refreshOnReturn = () => {
      if (document.visibilityState !== "visible") return;
      if (drawerOpen) return;
      if (Date.now() - lastListFetchRef.current < LIST_REFRESH_MIN_GAP_MS) return;

      lastListFetchRef.current = Date.now();
      fetchFeedbacks(pagination.current, statusFilter, searchText, customerTypeFilter, {
        silent: true,
      });
    };

    document.addEventListener("visibilitychange", refreshOnReturn);

    return () => {
      document.removeEventListener("visibilitychange", refreshOnReturn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, pagination.current, statusFilter, searchText, customerTypeFilter]);

  const handleSearch = () => {
    fetchFeedbacks(1, statusFilter, searchText);
  };

  const handleRefresh = () => {
    fetchFeedbacks(pagination.current, statusFilter, searchText);

    if (selectedThreadId) {
      fetchThreadDetail(selectedThreadId, false, true);
    }
  };

  const handleTableChange = (nextPagination) => {
    fetchFeedbacks(nextPagination.current, statusFilter, searchText);
  };

  // Làm mới tin nhắn của hội thoại đang xem (refresh nền, giữ nháp đang soạn).
  const handleRefreshThread = async () => {
    if (!selectedThreadId) return;
    setRefreshing(true);
    try {
      await fetchThreadDetail(selectedThreadId, false, true);
    } finally {
      setRefreshing(false);
    }
  };

  const resetReplyComposer = () => {
    setReplyText("");
    setAttachmentList([]);
  };

  // Tin chỉ có tệp đính kèm vẫn gửi được (BE chỉ chặn khi trống cả chữ lẫn tệp).
  const hasReplyContent = Boolean(replyText.trim()) || attachmentList.length > 0;

  const handleReply = async () => {
    if (!selectedThreadId || !hasReplyContent) {
      return;
    }

    setReplyLoading(true);

    try {
      const payload = buildReplyFormData({
        messageText: replyText,
        fileList: attachmentList,
      });

      const res = await feedbackApi.reply(selectedThreadId, payload);

      if (!isSuccessResponse(res)) {
        message.error(res?.data?.message || "Không thể gửi phản hồi");
        return;
      }

      message.success("Đã gửi phản hồi cho khách hàng");
      resetReplyComposer();
      await fetchThreadDetail(selectedThreadId, false, true);
      // Tin vừa thêm là của CHÍNH MÌNH -> không được tính vào badge "tin mới".
      setNewMessageCount(0);
      // silent: drawer đang mở, bảng phía sau chỉ cần dữ liệu mới chứ không cần spinner.
      await fetchFeedbacks(pagination.current, statusFilter, searchText, customerTypeFilter, {
        silent: true,
      });
    } catch (error) {
      message.error(error?.response?.data?.message || "Lỗi khi gửi phản hồi");
    } finally {
      setReplyLoading(false);
    }
  };

  /*
   * Chỉ còn 2 hành động thủ công: KẾT THÚC (CLOSED) và MỞ LẠI (OPEN).
   * Ba trạng thái chờ do BE tự đặt theo "ai vừa nhắn" nên không đưa ra UI —
   * đặt tay cũng bị tin nhắn kế tiếp ghi đè, mà vẫn bắn thông báo rác cho khách.
   */
  const handleUpdateStatus = async (nextStatus, note) => {
    if (!selectedThreadId) {
      return;
    }

    setStatusLoading(true);

    try {
      const res = await feedbackApi.updateStatus(selectedThreadId, {
        status: nextStatus,
        ...(note?.trim() ? { note: note.trim() } : {}),
      });

      if (!isSuccessResponse(res)) {
        message.error(res?.data?.message || "Không thể cập nhật trạng thái");
        return;
      }

      message.success(nextStatus === "CLOSED" ? "Đã kết thúc hội thoại" : "Đã mở lại hội thoại");
      await fetchThreadDetail(selectedThreadId, false, true);
      // Tin vừa thêm là của CHÍNH MÌNH -> không được tính vào badge "tin mới".
      setNewMessageCount(0);
      // silent: drawer đang mở, bảng phía sau chỉ cần dữ liệu mới chứ không cần spinner.
      await fetchFeedbacks(pagination.current, statusFilter, searchText, customerTypeFilter, {
        silent: true,
      });
    } catch (error) {
      message.error(error?.response?.data?.message || "Lỗi khi cập nhật trạng thái");
    } finally {
      setStatusLoading(false);
    }
  };

  /** Kết thúc là hành động khóa 2 chiều (khách cũng không nhắn tiếp được) -> phải xác nhận. */
  const confirmCloseThread = () => {
    let note = "";

    Modal.confirm({
      title: "Kết thúc hội thoại",
      icon: <ExclamationCircleOutlined style={{ color: semantic.warning }} />,
      content: (
        <div>
          <div style={{ marginBottom: 12 }}>
            Sau khi kết thúc, cả NSX và khách đều không gửi thêm tin nhắn vào hội thoại này được.
            Khách muốn trao đổi tiếp sẽ phải tạo phản ánh mới.
          </div>
          <Input.TextArea
            rows={2}
            placeholder="Lời nhắn gửi khách khi kết thúc (tùy chọn)"
            onChange={(e) => {
              note = e.target.value;
            }}
          />
        </div>
      ),
      okText: "Kết thúc",
      cancelText: "Hủy",
      onOk: () => handleUpdateStatus("CLOSED", note),
    });
  };

  const confirmReopenThread = () => {
    Modal.confirm({
      title: "Mở lại hội thoại",
      icon: <ExclamationCircleOutlined style={{ color: semantic.info }} />,
      content: "Hội thoại chuyển về \"Đang xử lý/trao đổi\", hai bên nhắn tiếp được. Khách sẽ nhận thông báo mở lại.",
      okText: "Mở lại",
      cancelText: "Hủy",
      onOk: () => handleUpdateStatus("OPEN"),
    });
  };

  /*
   * Dải chỉ số lấy từ `summary` của API (đếm trên toàn bộ hội thoại, không phải
   * theo trang đang xem) nên số liệu đúng ở mọi trang.
   */
  const stats = [
    {
      title: "Tổng hội thoại",
      value: (summary.total || 0).toLocaleString("vi-VN"),
      icon: <MessageOutlined style={{ fontSize: 18 }} />,
      color: brand.accent,
    },
    {
      title: "Chờ xử lý",
      value: (summary.waiting_nsx || 0).toLocaleString("vi-VN"),
      icon: <ClockCircleOutlined style={{ fontSize: 18 }} />,
      // Chỉ tô nền cảnh báo khi thực sự có việc tồn đang chờ mình xử lý.
      ...(summary.waiting_nsx > 0
        ? { color: semantic.warning, bgColor: semantic.warningSubtle, borderColor: "#FDE68A" }
        : { color: neutral[400] }),
    },
    {
      // Gộp đúng như nhãn trạng thái: đã trả lời khách (WAITING_CUSTOMER) + mở lại thủ công (OPEN).
      title: "Đang xử lý/trao đổi",
      value: ((summary.waiting_customer || 0) + (summary.open || 0)).toLocaleString("vi-VN"),
      icon: <CommentOutlined style={{ fontSize: 18 }} />,
      color: semantic.info,
    },
    {
      title: "Chưa đọc trên trang",
      value: (summary.unread_admin_total || 0).toLocaleString("vi-VN"),
      icon: <FileTextOutlined style={{ fontSize: 18 }} />,
      ...(summary.unread_admin_total > 0
        ? { color: semantic.danger, bgColor: semantic.dangerSubtle, borderColor: "#FECACA" }
        : { color: neutral[400] }),
    },
  ];

  /**
   * Xoá bộ lọc: nếu có ô select đang bật thì để `useEffect` fetch (React gộp 2 setState
   * thành 1 lần render -> 1 request), chỉ tự gọi khi không select nào đổi.
   */
  const clearFilters = () => {
    const hadSelectFilter = Boolean(statusFilter || customerTypeFilter);

    setSearchText("");
    if (statusFilter) setStatusFilter(undefined);
    if (customerTypeFilter) setCustomerTypeFilter(undefined);

    if (!hadSelectFilter) fetchFeedbacks(1, undefined, "", undefined);
  };

  const columns = [
    {
      title: "Hội thoại",
      dataIndex: "title",
      key: "title",
      width: 320,
      render: (_, record) => (
        <div>
          <Flex align="center" gap={8} style={{ marginBottom: 6 }}>
            <Typography.Text strong style={{ color: neutral[900] }}>
              {record.title || `Hội thoại #${record.id}`}
            </Typography.Text>
            {/* Chip chữ thay cho Badge số trơn: nhân sự lướt danh sách đọc ra ngay là
                "tin khách gửi chưa ai đọc", không phải hover mới hiểu. */}
            {record.unread_admin_count > 0 && (
              <span
                style={{
                  background: shinko.orange,
                  color: neutral[0],
                  fontSize: typography.size.xs,
                  fontWeight: typography.weight.semibold,
                  lineHeight: 1,
                  padding: "4px 8px",
                  borderRadius: radius.pill,
                  whiteSpace: "nowrap",
                }}
              >
                {`${record.unread_admin_count} tin mới`}
              </span>
            )}
          </Flex>
          {/* Ẩn #id: là khoá chính DB, không mang nghĩa nghiệp vụ cho người vận hành.
              Cần đối chiếu thì mở Drawer — tiêu đề Drawer vẫn giữ "Hội thoại #id". */}
          <Typography.Text
            style={{
              color: neutral[600],
              fontSize: typography.size.base,
              display: "block",
              lineHeight: 1.5,
            }}
          >
            {record.content || "Chưa có nội dung"}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: "Khách hàng",
      key: "customer",
      width: 220,
      render: (_, record) => (
        <div>
          <Typography.Text strong style={{ color: neutral[800], display: "block" }}>
            {record.customer?.name || "Khách chưa xác định"}
          </Typography.Text>
          <Typography.Text style={{ color: neutral[500], fontSize: typography.size.sm, display: "block" }}>
            {record.customer?.phone || "--"}
          </Typography.Text>
          <Typography.Text style={{ color: neutral[400], fontSize: typography.size.xs }}>
            {record.customer?.customer_code || ""}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: "Đối tượng",
      key: "customer_type",
      width: 130,
      align: "center",
      render: (_, record) => {
        const type = record.customer?.customer_type;
        if (!type) return <Typography.Text style={{ color: neutral[400] }}>--</Typography.Text>;

        return <StatusTag>{CUSTOMER_TYPE_LABELS[type] || type}</StatusTag>;
      },
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 180,
      render: (status) => {
        const meta = getStatusMeta(status);
        return (
          <StatusTag dot tone={meta.tone}>
            {meta.label}
          </StatusTag>
        );
      },
    },
    {
      title: "Mốc cuối",
      dataIndex: "last_message_at",
      key: "last_message_at",
      width: 180,
      render: (value, record) => (
        <div>
          <Typography.Text strong style={{ color: neutral[800], display: "block" }}>
            {value ? dayjs(value).format("DD/MM/YYYY") : "--"}
          </Typography.Text>
          <Typography.Text style={{ color: neutral[500], fontSize: typography.size.sm, display: "block" }}>
            {value ? dayjs(value).format("HH:mm") : ""}
          </Typography.Text>
          <Typography.Text style={{ color: neutral[400], fontSize: typography.size.xs }}>
            {record.last_message_by_type === "CUSTOMER"
              ? "Khách nhắn cuối"
              : record.last_message_by_type === "ADMIN"
                ? "NSX nhắn cuối"
                : "Hệ thống cập nhật"}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: "Thao tác",
      key: "actions",
      width: 100,
      align: "center",
      fixed: "right",
      render: (_, record) => <RowActions onView={() => fetchThreadDetail(record.id)} />,
    },
  ];

  return (
    <BrandedPageLayout
      title="Phản ánh khách hàng"
      subtitle="Theo dõi toàn bộ hội thoại tư vấn và khiếu nại giữa khách hàng với NSX trên miniapp."
      breadcrumbItems={[{ title: "Phản ánh khách hàng" }]}
    >
      <StatStrip stats={stats} loading={loading && feedbacks.length === 0} />

      <ListToolbar
        filterDefs={FILTER_DEFS}
        filters={{
          search: (
            <Input.Search
              allowClear
              enterButton
              placeholder="Tìm theo tiêu đề, nội dung, tên khách hoặc số điện thoại"
              prefix={<SearchOutlined style={{ color: neutral[400] }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
            />
          ),
          status: (
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Lọc trạng thái"
              value={statusFilter}
              onChange={(value) => setStatusFilter(value)}
              options={STATUS_OPTIONS}
            />
          ),
          customerType: (
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Thợ thầu / Đại lý"
              value={customerTypeFilter}
              onChange={(value) => setCustomerTypeFilter(value)}
              options={CUSTOMER_TYPE_OPTIONS}
            />
          ),
        }}
        onHideFilter={(key) => {
          if (key === "search") {
            setSearchText("");
            fetchFeedbacks(1, statusFilter, "");
          }
          // status/customerType: đổi về undefined là `useEffect` tự fetch lại.
          if (key === "status") setStatusFilter(undefined);
          if (key === "customerType") setCustomerTypeFilter(undefined);
        }}
        onClearFilters={clearFilters}
        loading={loading}
        onReload={handleRefresh}
        actions={
          <ImportExportMenu
            exportUrl="/api/v1/admin/feedback-threads/export"
            exportParams={{
              ...(statusFilter ? { status: statusFilter } : {}),
              ...(customerTypeFilter ? { customer_type: customerTypeFilter } : {}),
              ...(searchText?.trim() ? { search: searchText.trim() } : {}),
            }}
            exportFileName="phan_anh_khach_hang.xlsx"
            exportActions={["feedbacks.index"]}
          />
        }
      />

      <CmsTableCard
        columns={columns}
        dataSource={feedbacks}
        rowKey="id"
        loading={loading}
        onChange={handleTableChange}
        pagination={pagination}
        scroll={{ x: "max-content" }}
        locale={{
          emptyText: (
            <Empty
              description="Chưa có hội thoại nào khớp bộ lọc hiện tại"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />

      <Drawer
        title={selectedThread ? `Hội thoại #${selectedThread.id}` : "Chi tiết hội thoại"}
        width="min(760px, 100vw)"
        open={drawerOpen}
        onClose={() => {
          // Đóng drawer -> nhịp poll tự tắt (usePolling nhận enabled = false) và dọn badge;
          // tăng seq để phản hồi đang bay không ghi đè state của phiếu vừa đóng.
          detailSeqRef.current += 1;
          setDrawerOpen(false);
          setNewMessageCount(0);
        }}
        destroyOnClose={false}
        extra={
          selectedThread ? (
            <Space size={8} align="center">
              <Tooltip title="Làm mới tin nhắn">
                <Button
                  icon={<ReloadOutlined spin={refreshing} />}
                  onClick={handleRefreshThread}
                  loading={refreshing}
                />
              </Tooltip>
              <StatusTag dot tone={getStatusMeta(selectedThread.status).tone}>
                {getStatusMeta(selectedThread.status).label}
              </StatusTag>
              {selectedThread.status === "CLOSED" ? (
                <Button
                  icon={<RollbackOutlined />}
                  loading={statusLoading}
                  onClick={confirmReopenThread}
                >
                  Mở lại trao đổi
                </Button>
              ) : (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={statusLoading}
                  onClick={confirmCloseThread}
                >
                  Kết thúc
                </Button>
              )}
            </Space>
          ) : null
        }
        styles={{
          body: {
            padding: 0,
            background: neutral[50],
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
        }}
      >
        {detailLoading || !selectedThread ? (
          <div style={{ padding: spacing[6] }}>
            <Card loading bordered={false} />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            {/* Header danh tính gọn */}
            <div style={{ background: neutral[0], borderBottom: border.hairline, padding: `${spacing[4]}px ${spacing[6]}px` }}>
              <Flex justify="space-between" align="flex-start" gap={12} wrap="wrap">
                <div style={{ minWidth: 0 }}>
                  <Title level={5} style={{ margin: 0 }}>
                    {selectedThread.title || `Hội thoại #${selectedThread.id}`}
                  </Title>
                  <Typography.Text style={{ color: neutral[500], fontSize: typography.size.sm, display: "block", marginTop: 2 }}>
                    {selectedThread.customer?.name || "Khách chưa xác định"}
                    {" · "}
                    {selectedThread.customer?.phone || "--"}
                    {selectedThread.customer?.customer_code ? ` · ${selectedThread.customer.customer_code}` : ""}
                  </Typography.Text>
                </div>
                <StatusTag dot tone={getStatusMeta(selectedThread.status).tone}>
                  {getStatusMeta(selectedThread.status).label}
                </StatusTag>
              </Flex>
              {selectedThread.last_message_at && (
                <Typography.Text style={{ color: neutral[400], fontSize: typography.size.sm, display: "block", marginTop: spacing[2] }}>
                  Tin nhắn cuối: {dayjs(selectedThread.last_message_at).format("DD/MM/YYYY HH:mm")}
                </Typography.Text>
              )}
            </div>

            {/* Khu vực hội thoại — chat, chiếm phần lớn, cuộn riêng */}
            <div
              ref={messagesRef}
              style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: spacing[6] }}
            >
              {(selectedThread.messages || []).length === 0 ? (
                <Empty description="Hội thoại chưa có tin nhắn" style={{ marginTop: spacing[8] }} />
              ) : (
                <Space direction="vertical" size={spacing[4]} style={{ width: "100%" }}>
                  {(selectedThread.messages || []).map((item) => {
                    const isAdmin = item.sender_type === "ADMIN" || item.is_internal_note;
                    const isSystem = item.sender_type === "SYSTEM";
                    const align = isSystem ? "center" : isAdmin ? "flex-end" : "flex-start";
                    return (
                      <div key={item.id} style={{ display: "flex", flexDirection: "column", alignItems: align }}>
                        <div style={{ maxWidth: "80%", minWidth: 0 }}>
                          <Flex align="center" gap={8} style={{ marginBottom: 4, justifyContent: isAdmin ? "flex-end" : "flex-start" }}>
                            <Typography.Text strong style={{ color: neutral[700], fontSize: typography.size.sm }}>
                              {getSenderLabel(item)}
                            </Typography.Text>
                            {item.is_internal_note && <StatusTag>Nội bộ</StatusTag>}
                            <Typography.Text style={{ color: neutral[400], fontSize: typography.size.xs }}>
                              {item.created_at ? dayjs(item.created_at).format("DD/MM/YYYY HH:mm") : ""}
                            </Typography.Text>
                          </Flex>
                          {item.message?.trim() ? (
                            <div style={{ borderRadius: 10, padding: `${spacing[3]}px ${spacing[4]}px`, ...getMessageBubbleStyle(item) }}>
                              <Typography.Text style={{ color: "inherit", whiteSpace: "pre-wrap", lineHeight: typography.leading.relaxed }}>
                                {item.message}
                              </Typography.Text>
                            </div>
                          ) : null}
                          {item.attachments?.length > 0 && (
                            <Space wrap size={[8, 8]} style={{ marginTop: spacing[2], display: "flex", justifyContent: isAdmin ? "flex-end" : "flex-start" }}>
                              {item.attachments.map((attachment) => (
                                <a
                                  key={attachment.id}
                                  href={attachment.file_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "6px 10px",
                                    borderRadius: 999,
                                    border: border.hairline,
                                    background: neutral[0],
                                    color: brand.accent,
                                    textDecoration: "none",
                                    fontSize: typography.size.sm,
                                  }}
                                >
                                  <PaperClipOutlined />
                                  {attachment.file_name}
                                </a>
                              ))}
                            </Space>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Space>
              )}
            </div>

            {/*
              Tin mới do nhịp poll mang về: KHÔNG tự cuộn tới, vì có thể nhân sự đang đọc lại
              đoạn giữa hoặc đang soạn. Hiện một dải bấm được để họ tự quyết định.
            */}
            {newMessageCount > 0 && (
              <div
                style={{
                  background: neutral[0],
                  borderTop: border.hairline,
                  padding: `${spacing[2]}px ${spacing[5]}px`,
                }}
              >
                <Button type="link" size="small" style={{ padding: 0 }} onClick={handleSeeNewMessages}>
                  {`Có ${newMessageCount} tin mới — bấm để xem`}
                </Button>
              </div>
            )}

            {/* Composer dính đáy */}
            <div style={{ background: neutral[0], borderTop: border.hairline, padding: spacing[5] }}>
              {selectedThread.status === "CLOSED" ? (
                <Typography.Text style={{ color: neutral[500], fontSize: typography.size.sm }}>
                  Hội thoại đã đóng. Muốn trả lời tiếp, đổi trạng thái sang “Đang mở” ở góc trên.
                </Typography.Text>
              ) : (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <Input.TextArea
                    rows={3}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Nhập nội dung phản hồi để gửi cho khách hàng..."
                  />
                  <Flex justify="space-between" align="center" gap={12} wrap="wrap">
                    <Upload
                      multiple
                      fileList={attachmentList}
                      beforeUpload={() => false}
                      onChange={({ fileList }) => setAttachmentList(fileList.slice(0, 5))}
                      onRemove={(file) => setAttachmentList((prev) => prev.filter((i) => i.uid !== file.uid))}
                    >
                      <Button icon={<UploadOutlined />}>Đính kèm</Button>
                    </Upload>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      loading={replyLoading}
                      disabled={!hasReplyContent}
                      onClick={handleReply}
                    >
                      Gửi phản hồi
                    </Button>
                  </Flex>
                </Space>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </BrandedPageLayout>
  );
};

export default FeedbackList;
